import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardHeader,
  Checkbox,
  CircularProgress,
  IconButton,
  MenuItem,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Iconify from '../../../components/Iconify';
import Label from '../../../components/Label';
import { authApiCall } from '../../../services/http';
import { endpoints } from '../../../config';
import { setAuthToken, setAuthUser } from '../../../redux/reducers/authSlice';
import { stripeSubscriptionUrl, subHolder, subOccupant, subscriptionStatus } from '../../../helpers/subscriptions';
import { productLabel } from '../../../helpers/products';
import copyToClipboard from '../../../utils/copyToClipboard';
import useProducts from '../../../hooks/useProducts';
import SubscriptionUsersDialog from './SubscriptionUsersDialog';
import SelectionMetrics from './SelectionMetrics';
import CreateSubscriptionDialog from '../../../layouts/dialogs/CreateSubscriptionDialog';
import UserActiveSubscriptionsDialog from '../../../layouts/dialogs/UserActiveSubscriptionsDialog';
import DebugPanel from './DebugPanel';

const fDate = (iso) => (iso ? new Date(iso).toLocaleDateString('es-ES') : '—');

// `mobileHide`: ocultar la columna en xs/sm para que la tabla quepa mejor en móvil.
const HEAD = [
  { id: 'id', label: 'ID' },
  { id: 'status', label: 'Estado', sortable: true },
  { id: 'stripeSubscriptionId', label: 'Stripe Sub' },
  // `holder` (de quién es la sub) y `occupant` (quién la usa) son dos personas distintas y el
  // backend ya no las mezcla en un único `user`. Ver helpers/subscriptions.
  { id: 'holder', label: 'Titular', mobileHide: true },
  { id: 'occupant', label: 'Ocupante', mobileHide: true },
  { id: 'workspace', label: 'Workspace', mobileHide: true },
  { id: 'seats', label: 'Asientos', align: 'right' },
  { id: 'comments', label: 'Comentarios', mobileHide: true },
  { id: 'createdAt', label: 'Creada', sortable: true, mobileHide: true },
];

const hideOnMobile = { display: { xs: 'none', md: 'table-cell' } };

// Columna final con el botón "Abrir". Casi todas las celdas paran la propagación del click (copiar,
// filtrar, comentar), así que abrir la sub clicando la fila era una lotería. En desktop con ratón el
// botón aparece al hacer hover sobre la fila; en móvil/táctil se ve siempre, pegado a la derecha
// (sticky) por si la tabla hace scroll horizontal.
const OPEN_ACTION_CLASS = 'sub-row-open-action';
const openActionCellSx = (theme) => ({
  width: 48,
  pl: 0,
  [theme.breakpoints.down('md')]: {
    position: 'sticky',
    right: 0,
    zIndex: 1,
    bgcolor: 'background.paper',
  },
});
const openActionButtonSx = (theme) => ({
  transition: theme.transitions.create('opacity', { duration: theme.transitions.duration.shorter }),
  [`${theme.breakpoints.up('md')} and (hover: hover)`]: {
    opacity: 0,
    '&:focus-visible': { opacity: 1 },
  },
});

SubscriptionsListTable.propTypes = {
  stripeStatsLookup: PropTypes.instanceOf(Map),
  stats: PropTypes.object,
};

export default function SubscriptionsListTable({ stripeStatsLookup, stats }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authToken = useSelector((state) => state.auth.authToken);
  // Filtros iniciales desde la URL (deep-link desde la tabla de usuarios: ?userId / ?workspaceId).
  const [searchParams] = useSearchParams();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const [page, setPage] = useState(0); // TablePagination es 0-based, el endpoint es 1-based
  const [pageSize, setPageSize] = useState(20);
  const [status, setStatus] = useState(() => searchParams.get('status') || '');
  const [search, setSearch] = useState(() => searchParams.get('search') || '');
  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') || '');
  // Filtros por id interno (enteros). userId = titular individual de la sub (no ocupante de seat).
  const [userId, setUserId] = useState(() => searchParams.get('userId') || '');
  const [userIdInput, setUserIdInput] = useState(() => searchParams.get('userId') || '');
  const [workspaceId, setWorkspaceId] = useState(() => searchParams.get('workspaceId') || '');
  const [workspaceIdInput, setWorkspaceIdInput] = useState(() => searchParams.get('workspaceId') || '');
  // El input de userId/workspaceId solo se renderiza con el `ownerType` correspondiente, así que
  // en un deep-link lo inferimos del parámetro que venga: si no, el filtro se aplicaría de forma
  // invisible y el admin no podría ni verlo ni editarlo.
  const [ownerType, setOwnerType] = useState(() => {
    const fromUrl = searchParams.get('ownerType');
    if (fromUrl) return fromUrl;
    if (searchParams.get('userId')) return 'individual';
    if (searchParams.get('workspaceId')) return 'team';
    return '';
  });
  // `orphan`: subs sin ningún seat ocupado por un usuario vivo (userCount === 0). El backend solo
  // acepta las cadenas 'true' | 'false' (o nada); cualquier otra cosa es un 400.
  const [orphan, setOrphan] = useState(() => searchParams.get('orphan') || '');
  // Modo de búsqueda del campo de texto libre: '' = OR por defecto (publicId, ids de Stripe y,
  // desde el último cambio de back, también email), 'userEmail' = match estricto por email.
  const [searchBy, setSearchBy] = useState(() => searchParams.get('searchBy') || '');
  const [planSlug, setPlanSlug] = useState('');
  const { plans: planOptions, loading: plansLoading } = useProducts();
  const [orderBy, setOrderBy] = useState('createdAt');
  const [orderDir, setOrderDir] = useState('DESC');

  const [openSub, setOpenSub] = useState(null);

  // Chequeo post-creación: titular de la sub recién creada + su id, para excluirla del listado
  // de "otras subs vivas" (no es un conflicto consigo misma).
  const [conflictUserId, setConflictUserId] = useState(null);
  const [createdSubId, setCreatedSubId] = useState(null);

  // Selección persistente entre páginas: guardamos el snapshot del item para poder calcular
  // métricas sobre subs que ya no estén en la página visible.
  const [selectedById, setSelectedById] = useState(() => new Map());

  const toggleOne = (sub) => {
    setSelectedById((prev) => {
      const next = new Map(prev);
      if (next.has(sub.id)) next.delete(sub.id);
      else next.set(sub.id, sub);
      return next;
    });
  };

  const togglePage = () => {
    setSelectedById((prev) => {
      const next = new Map(prev);
      const allSelected = items.length > 0 && items.every((it) => next.has(it.id));
      if (allSelected) {
        items.forEach((it) => next.delete(it.id));
      } else {
        items.forEach((it) => next.set(it.id, it));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedById(new Map());

  const selectedItems = Array.from(selectedById.values());
  const pageSelectedCount = items.filter((it) => selectedById.has(it.id)).length;
  const allPageSelected = items.length > 0 && pageSelectedCount === items.length;
  const somePageSelected = pageSelectedCount > 0 && !allPageSelected;

  const [commentAnchor, setCommentAnchor] = useState(null);
  const [commentRow, setCommentRow] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [savingComment, setSavingComment] = useState(false);
  const [editError, setEditError] = useState(null);

  const openCommentEditor = (e, r) => {
    setCommentAnchor(e.currentTarget);
    setCommentRow(r);
    setEditingValue(r.comments || '');
    setEditError(null);
  };

  const closeCommentEditor = () => {
    if (savingComment) return;
    setCommentAnchor(null);
    setCommentRow(null);
    setEditingValue('');
    setEditError(null);
  };

  const saveComment = async () => {
    if (!commentRow) return;
    const next = editingValue.trim() === '' ? null : editingValue;
    if (next === (commentRow.comments ?? null)) {
      closeCommentEditor();
      return;
    }
    setSavingComment(true);
    setEditError(null);
    try {
      const res = await authApiCall({
        method: 'PUT',
        endpoint: endpoints.subscriptions,
        path: `/${commentRow.id}`,
        data: { comments: next },
        navigate,
        dispatch,
        setAuthUser,
        setAuthToken,
        authToken,
        redirectRoute: null,
      });
      const updated = res?.data || { ...commentRow, comments: next };
      setItems((prev) => prev.map((it) => (it.id === commentRow.id ? { ...it, ...updated } : it)));
      setSavingComment(false);
      setCommentAnchor(null);
      setCommentRow(null);
      setEditingValue('');
      setEditError(null);
    } catch (e) {
      setEditError(e?.response?.data?.error || e?.message || 'Error al guardar');
      setSavingComment(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page + 1));
      params.set('pageSize', String(pageSize));
      if (status) params.set('status', status);
      if (search) params.set('search', search);
      if (search && searchBy) params.set('searchBy', searchBy);
      if (userId) params.set('userId', userId);
      if (workspaceId) params.set('workspaceId', workspaceId);
      if (ownerType) params.set('ownerType', ownerType);
      if (orphan) params.set('orphan', orphan);
      if (planSlug) params.set('planSlug', planSlug);
      params.set('orderBy', orderBy);
      params.set('orderDir', orderDir);

      const res = await authApiCall({
        method: 'GET',
        endpoint: endpoints.subscriptions,
        path: `/list?${params.toString()}`,
        navigate,
        dispatch,
        setAuthUser,
        setAuthToken,
        authToken,
        redirectRoute: null,
      });
      setItems(res?.data?.items || []);
      setTotal(res?.data?.total || 0);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Error cargando suscripciones');
    } finally {
      setLoading(false);
    }
  }, [
    authToken,
    dispatch,
    navigate,
    page,
    pageSize,
    status,
    search,
    searchBy,
    userId,
    workspaceId,
    ownerType,
    orphan,
    planSlug,
    orderBy,
    orderDir,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSort = (id) => {
    if (orderBy === id) {
      setOrderDir(orderDir === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setOrderBy(id);
      setOrderDir('DESC');
    }
    setPage(0);
  };

  const handleSearch = () => {
    setSearch(searchInput.trim());
    setUserId(userIdInput.trim());
    setWorkspaceId(workspaceIdInput.trim());
    setPage(0);
  };

  // Filtrado rápido desde la tabla: click en el #id de titular/workspace.
  // Fijamos también el `ownerType` correspondiente porque es el único que renderiza el input:
  // sin él, el filtro quedaría aplicado pero invisible. Combinarlos ya filtra de verdad en backend
  // (antes `ownerType` pisaba a `userId` y devolvía todas las individuales).
  const filterByUser = (id) => {
    const v = String(id);
    setOwnerType('individual');
    setWorkspaceId('');
    setWorkspaceIdInput('');
    setUserIdInput(v);
    setUserId(v);
    setPage(0);
  };

  const filterByWorkspace = (id) => {
    const v = String(id);
    setOwnerType('team');
    setUserId('');
    setUserIdInput('');
    setWorkspaceIdInput(v);
    setWorkspaceId(v);
    setPage(0);
  };

  // El ocupante no se puede filtrar con `userId` (eso filtra por TITULAR), así que tiramos de la
  // búsqueda estricta por email, que cubre tanto titulares como ocupantes de seats.
  const filterByEmail = (email) => {
    if (!email) return;
    setOwnerType('');
    setUserId('');
    setUserIdInput('');
    setWorkspaceId('');
    setWorkspaceIdInput('');
    setSearchBy('userEmail');
    setSearchInput(email);
    setSearch(email);
    setPage(0);
  };

  const clearFilters = () => {
    setSearch('');
    setSearchInput('');
    setSearchBy('');
    setStatus('');
    setUserId('');
    setUserIdInput('');
    setWorkspaceId('');
    setWorkspaceIdInput('');
    setOwnerType('');
    setOrphan('');
    setPlanSlug('');
    setPage(0);
  };

  return (
    <Card>
      <CardHeader
        title="Listado de suscripciones"
        subheader={`${total} en BD`}
        action={
          <Button
            variant="contained"
            startIcon={<Iconify icon="eva:plus-fill" width={18} height={18} />}
            onClick={() => setCreateOpen(true)}
          >
            Crear suscripción
          </Button>
        }
      />

      {stats && <DebugPanel stats={stats} />}

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1}
        alignItems={{ xs: 'stretch', md: 'center' }}
        sx={{ p: 2, pt: 1 }}
        flexWrap="wrap"
        useFlexGap
      >
        <TextField
          select
          label="Estado"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(0);
          }}
          size="small"
          sx={{ minWidth: { md: 140 } }}
        >
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="unpaid">Unpaid</MenuItem>
          <MenuItem value="depleted">Depleted</MenuItem>
          <MenuItem value="canceled">Canceled</MenuItem>
        </TextField>

        <TextField
          select
          label="Tipo"
          value={ownerType}
          onChange={(e) => {
            const v = e.target.value;
            setOwnerType(v);
            // Solo `individual` conserva userId y solo `team` conserva workspaceId; `unassigned`
            // (ni titular ni workspace) no admite ninguno de los dos.
            if (v !== 'team') {
              setWorkspaceId('');
              setWorkspaceIdInput('');
            }
            if (v !== 'individual') {
              setUserId('');
              setUserIdInput('');
            }
            if (v === 'individual' || v === 'team') {
              setSearch('');
              setSearchInput('');
            }
            setPage(0);
          }}
          size="small"
          sx={{ minWidth: { md: 140 } }}
        >
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="individual">Individual</MenuItem>
          <MenuItem value="team">Equipo</MenuItem>
          <MenuItem value="unassigned">Sin asignar</MenuItem>
        </TextField>

        <Tooltip title="Huérfana = sin ningún seat ocupado por un usuario vivo (0 asientos usados).">
          <TextField
            select
            label="Ocupación"
            value={orphan}
            onChange={(e) => {
              setOrphan(e.target.value);
              setPage(0);
            }}
            size="small"
            sx={{ minWidth: { md: 160 } }}
          >
            <MenuItem value="">Todas</MenuItem>
            <MenuItem value="true">Solo huérfanas</MenuItem>
            <MenuItem value="false">Solo con ocupante</MenuItem>
          </TextField>
        </Tooltip>

        {(!ownerType || ownerType === 'unassigned') && (
          <>
            <TextField
              select
              label="Buscar por"
              value={searchBy}
              onChange={(e) => {
                setSearchBy(e.target.value);
                setPage(0);
              }}
              size="small"
              sx={{ minWidth: { md: 130 } }}
            >
              <MenuItem value="">Auto</MenuItem>
              <MenuItem value="userEmail">Email</MenuItem>
            </TextField>
            <TextField
              label={searchBy === 'userEmail' ? 'Email del usuario' : 'StripeCustomerId/StripeSubId/SubId/Email'}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              size="small"
              placeholder={searchBy === 'userEmail' ? 'alguien@empresa.com' : 'sub_..., cus_..., uuid, email'}
              sx={{ flex: { md: 1 }, minWidth: { md: 240 } }}
            />
          </>
        )}

        {ownerType === 'individual' && (
          <Tooltip title="ID interno (entero) del usuario titular de la sub. Ojo: filtra por titular, no por ocupante de seat.">
            <TextField
              label="ID Usuario (titular)"
              type="number"
              value={userIdInput}
              onChange={(e) => setUserIdInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              size="small"
              placeholder="123"
              sx={{ flex: { md: 1 }, minWidth: { md: 160 } }}
            />
          </Tooltip>
        )}

        {ownerType === 'team' && (
          <Tooltip title="ID interno (entero) del workspace. Devuelve todas las subs de ese equipo.">
            <TextField
              label="ID Workspace"
              type="number"
              value={workspaceIdInput}
              onChange={(e) => setWorkspaceIdInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              size="small"
              placeholder="456"
              sx={{ flex: { md: 1 }, minWidth: { md: 150 } }}
            />
          </Tooltip>
        )}

        <Autocomplete
          options={planOptions}
          loading={plansLoading}
          value={planOptions.find((p) => p.slug === planSlug) || null}
          onChange={(e, v) => {
            setPlanSlug(v?.slug || '');
            setPage(0);
          }}
          getOptionLabel={productLabel}
          isOptionEqualToValue={(o, v) => o.slug === v.slug}
          size="small"
          sx={{ minWidth: { md: 180 } }}
          renderInput={(params) => <TextField {...params} label="Plan" placeholder="Filtrar por plan" />}
        />

        <Stack direction="row" spacing={1}>
          <Button variant="contained" onClick={handleSearch} fullWidth sx={{ flex: { xs: 1, md: 'none' } }}>
            Buscar
          </Button>
          {(search || status || userId || workspaceId || ownerType || orphan || planSlug) && (
            <Button onClick={clearFilters} fullWidth sx={{ flex: { xs: 1, md: 'none' } }}>
              Limpiar
            </Button>
          )}
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mx: 2, mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* La foto completa de huérfanas son DOS consultas: las individuales sin ocupante vivo y las
          que no tienen ni titular ni workspace. El endpoint no las devuelve juntas. */}
      {orphan === 'true' && ownerType !== 'unassigned' && (
        <Alert
          severity="info"
          sx={{ mx: 2, mb: 2 }}
          action={
            <Button
              size="small"
              onClick={() => {
                setOwnerType('unassigned');
                setUserId('');
                setUserIdInput('');
                setWorkspaceId('');
                setWorkspaceIdInput('');
                setPage(0);
              }}
            >
              Ver sin asignar
            </Button>
          }
        >
          Esto no incluye las subs sin titular ni workspace: son una consulta aparte (Tipo = Sin asignar).
        </Alert>
      )}

      <SelectionMetrics items={selectedItems} onClear={clearSelection} stripeStatsLookup={stripeStatsLookup} />

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  indeterminate={somePageSelected}
                  checked={allPageSelected}
                  onChange={togglePage}
                  disabled={items.length === 0}
                  inputProps={{ 'aria-label': 'Seleccionar todas las de la página' }}
                />
              </TableCell>
              {HEAD.map((h) => (
                <TableCell key={h.id} align={h.align || 'left'} sx={h.mobileHide ? hideOnMobile : undefined}>
                  {h.sortable ? (
                    <TableSortLabel
                      active={orderBy === h.id}
                      direction={orderBy === h.id ? orderDir.toLowerCase() : 'desc'}
                      onClick={() => handleSort(h.id)}
                    >
                      {h.label}
                    </TableSortLabel>
                  ) : (
                    h.label
                  )}
                </TableCell>
              ))}
              <TableCell sx={openActionCellSx} />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={HEAD.length + 2} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={HEAD.length + 2} align="center" sx={{ py: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    No hay resultados.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((r) => {
                const st = subscriptionStatus(r);
                const seatsLabel = r.maxSeats ? `${r.userCount} / ${r.maxSeats}` : `${r.userCount}`;
                const overSeats = r.maxSeats && r.userCount > r.maxSeats;
                const isSelected = selectedById.has(r.id);
                const holder = subHolder(r);
                const occupant = subOccupant(r);
                return (
                  <TableRow
                    key={r.id}
                    hover
                    selected={isSelected}
                    sx={{ cursor: 'pointer', [`&:hover .${OPEN_ACTION_CLASS}`]: { opacity: 1 } }}
                    onClick={() => setOpenSub(r)}
                  >
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        size="small"
                        checked={isSelected}
                        onChange={() => toggleOne(r)}
                        inputProps={{ 'aria-label': `Seleccionar sub ${r.id}` }}
                      />
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Tooltip title={`Click para copiar ID — ${r.id}`}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          onClick={() => copyToClipboard(r.id)}
                          sx={{
                            fontFamily: 'monospace',
                            cursor: 'pointer',
                            '&:hover': { color: 'text.primary' },
                          }}
                        >
                          {String(r.id).slice(0, 8)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Label color={st.color}>{st.label}</Label>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {r.stripeSubscriptionId ? (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Tooltip title="Click para copiar ID">
                            <Typography
                              variant="body2"
                              onClick={() => copyToClipboard(r.stripeSubscriptionId)}
                              sx={{
                                fontFamily: 'monospace',
                                cursor: 'pointer',
                                '&:hover': { color: 'text.primary' },
                                color: 'text.secondary',
                              }}
                            >
                              {r.stripeSubscriptionId}
                            </Typography>
                          </Tooltip>
                          <Tooltip title="Ver en Stripe">
                            <IconButton
                              size="small"
                              component="a"
                              href={stripeSubscriptionUrl(r.stripeSubscriptionId)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Iconify icon="eva:external-link-outline" width={16} height={16} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      ) : (
                        <Label color="default">No stripe</Label>
                      )}
                    </TableCell>
                    <TableCell sx={hideOnMobile} onClick={(e) => e.stopPropagation()}>
                      {holder ? (
                        <Stack spacing={0}>
                          <Typography variant="body2" noWrap>
                            {holder.email || holder.givenName || '—'}
                          </Typography>
                          <Tooltip title="Filtrar subs de las que este usuario es titular">
                            <Typography
                              variant="caption"
                              color="text.disabled"
                              onClick={() => filterByUser(holder.id)}
                              sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                            >
                              #{holder.id}
                            </Typography>
                          </Tooltip>
                        </Stack>
                      ) : (
                        <Tooltip title="Sin titular: sub de equipo (2+ ocupantes) o sin asignar.">
                          <Typography variant="body2" color="text.disabled">
                            —
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell sx={hideOnMobile} onClick={(e) => e.stopPropagation()}>
                      {occupant ? (
                        <Stack spacing={0}>
                          <Tooltip title="Buscar todas las subs de este email (como titular o como ocupante)">
                            <Typography
                              variant="body2"
                              noWrap
                              onClick={() => filterByEmail(occupant.email)}
                              sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                            >
                              {occupant.email || occupant.givenName || '—'}
                            </Typography>
                          </Tooltip>
                          <Typography variant="caption" color="text.disabled">
                            #{occupant.id}
                          </Typography>
                        </Stack>
                      ) : (
                        <Tooltip title="Sin ocupante: huérfana, o de equipo con 2+ ocupantes.">
                          <Typography variant="body2" color="text.disabled">
                            —
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell sx={hideOnMobile} onClick={(e) => e.stopPropagation()}>
                      {r.workspace ? (
                        <Stack spacing={0}>
                          <Typography variant="body2" noWrap>
                            {r.workspace.name || '—'}
                          </Typography>
                          <Tooltip title="Filtrar subs de este workspace">
                            <Typography
                              variant="caption"
                              color="text.disabled"
                              onClick={() => filterByWorkspace(r.workspace.id)}
                              sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                            >
                              #{r.workspace.id}
                            </Typography>
                          </Tooltip>
                        </Stack>
                      ) : (
                        r.workspaceId ?? '—'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color={overSeats ? 'error.main' : 'text.primary'}>
                        {seatsLabel}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 280, ...hideOnMobile }} onClick={(e) => e.stopPropagation()}>
                      {r.comments ? (
                        <Tooltip title="Click para ver/editar">
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            onClick={(e) => openCommentEditor(e, r)}
                            sx={{
                              cursor: 'pointer',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              '&:hover': { color: 'text.primary' },
                            }}
                          >
                            {r.comments}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Click para añadir comentario">
                          <Typography
                            variant="body2"
                            color="text.disabled"
                            onClick={(e) => openCommentEditor(e, r)}
                            sx={{ cursor: 'pointer', '&:hover': { color: 'text.secondary' } }}
                          >
                            +
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell sx={hideOnMobile}>{fDate(r.createdAt)}</TableCell>
                    <TableCell align="right" sx={openActionCellSx}>
                      <Tooltip title="Abrir suscripción">
                        <IconButton
                          size="small"
                          className={OPEN_ACTION_CLASS}
                          aria-label={`Abrir sub ${r.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenSub(r);
                          }}
                          sx={openActionButtonSx}
                        >
                          <Iconify icon="eva:arrow-ios-forward-fill" width={18} height={18} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ position: 'relative' }}>
        {loading && items.length > 0 && (
          <Box sx={{ position: 'absolute', top: 8, left: 16 }}>
            <CircularProgress size={18} />
          </Box>
        )}
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </Box>

      <Popover
        open={Boolean(commentAnchor)}
        anchorEl={commentAnchor}
        onClose={closeCommentEditor}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { p: 2, width: 420 } }}
      >
        <Stack spacing={1.5}>
          <Typography variant="subtitle2">Comentario</Typography>
          <TextField
            value={editingValue}
            onChange={(e) => setEditingValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                saveComment();
              } else if (e.key === 'Escape') {
                closeCommentEditor();
              }
            }}
            multiline
            minRows={4}
            maxRows={10}
            fullWidth
            autoFocus
            disabled={savingComment}
            placeholder="Añade un comentario…"
          />
          {editError && (
            <Typography variant="caption" color="error.main">
              {editError}
            </Typography>
          )}
          <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
            <Typography variant="caption" color="text.disabled">
              ⌘/Ctrl + Enter para guardar · Esc para cancelar
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button onClick={closeCommentEditor} disabled={savingComment}>
                Cancelar
              </Button>
              <Button variant="contained" onClick={saveComment} disabled={savingComment}>
                {savingComment ? 'Guardando…' : 'Guardar'}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Popover>

      <SubscriptionUsersDialog
        open={Boolean(openSub)}
        subscription={openSub}
        onClose={() => setOpenSub(null)}
        onUpdated={(updated) => {
          if (updated && updated.id) {
            setItems((prev) => prev.map((it) => (it.id === updated.id ? { ...it, ...updated } : it)));
            setOpenSub((prev) => (prev && prev.id === updated.id ? { ...prev, ...updated } : prev));
            setSelectedById((prev) => {
              if (!prev.has(updated.id)) return prev;
              const next = new Map(prev);
              next.set(updated.id, { ...next.get(updated.id), ...updated });
              return next;
            });
          }
        }}
      />
      <CreateSubscriptionDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(createdSub) => {
          // Recargamos el listado para que aparezca la nueva sub.
          setPage(0);
          load();
          // Y abrimos su drill-in para añadir el primer seat directamente.
          // Necesita el id (UUID) que devuelve el backend; si no viniera, solo refrescamos.
          if (createdSub && createdSub.id) setOpenSub(createdSub);
          // Sub individual: comprobamos si el titular arrastraba otras subs vivas (trial sin
          // cancelar, etc.). Se abre encima del drill-in; al cerrarla queda el drill-in detrás.
          if (createdSub && createdSub.userId) {
            setCreatedSubId(createdSub.id || null);
            setConflictUserId(createdSub.userId);
          }
        }}
      />

      <UserActiveSubscriptionsDialog
        open={Boolean(conflictUserId)}
        userId={conflictUserId}
        excludeSubscriptionId={createdSubId}
        contextMessage={`Acabas de crear una suscripción para el usuario #${conflictUserId}.`}
        onClose={() => {
          setConflictUserId(null);
          setCreatedSubId(null);
          // El estado de las otras subs puede haber cambiado desde el diálogo (p. ej. cancelar
          // el trial), así que refrescamos el listado al volver.
          load();
        }}
      />
    </Card>
  );
}
